/**
 * AI Suggestions Service
 * 
 * This service manages AI-suggested assurance tasks.
 * Stores suggestions in /AI/assurance/suggestions
 * 
 * Updated: 2025-12-17 - Migrated to Thalassa API
 */

import { SuggestedAssuranceTask } from '@/components/update-cells/types';
import { deiterateAPI } from '@/js/external-js/variableModules/utils/deiterateAPI.util';

const AI_SUGGESTIONS_API = '/v1/ai-suggestions';

// Helper to get headers
function getHeaders() {
  const companyId = window.companyID;
  return {
    'X-Tenancy-Id': companyId,
    'Company-ID': companyId,
  };
}

/**
 * Get all AI-suggested tasks via Thalassa API
 * @returns {Promise<SuggestedAssuranceTask[]>} Array of suggestion documents
 */
export const getSuggestedTasks = async (): Promise<SuggestedAssuranceTask[]> => {
  console.log('[AISuggestionsService] Getting suggested tasks via Thalassa API');
  
  try {
    const companyId = window.companyID;
    console.log(`[AISuggestionsService] companyId=${companyId}`);

    const response = await deiterateAPI.GET(AI_SUGGESTIONS_API, {
      mode: 'cors',
      backend: 'thalassa',
      headers: getHeaders(),
    });

    if (response.statusCode === 200 && response.suggestions) {
      console.log(`[AISuggestionsService] Found ${response.suggestions.length} AI suggestions`);
      return response.suggestions;
    }
    
    console.log('[AISuggestionsService] No AI suggestions found');
    return [];
  } catch (error: any) {
    console.error('[AISuggestionsService] Error getting AI suggestions:', error);
    return [];
  }
};

/**
 * Accept a suggested task via Thalassa API
 * @param {string} id - ID of the suggested task to accept
 * @returns {Promise<{success: boolean}>} Result of the operation
 */
export const acceptSuggestedTask = async (id: string): Promise<{success: boolean}> => {
  console.log(`[AISuggestionsService] Accepting suggested task ${id} via Thalassa API`);
  
  try {
    const response = await deiterateAPI.PUT(`${AI_SUGGESTIONS_API}/${id}/accept`, {
      mode: 'cors',
      backend: 'thalassa',
      headers: getHeaders(),
    });

    if (response.statusCode === 200) {
      console.log(`[AISuggestionsService] Successfully marked suggestion ${id} as accepted`);
      return { success: true };
    }
    
    console.error('[AISuggestionsService] Error accepting suggestion:', response.error);
    return { success: false };
  } catch (error) {
    console.error('[AISuggestionsService] Error accepting suggestion:', error);
    return { success: false };
  }
};

/**
 * Reject a suggested task via Thalassa API
 * @param {string} id - ID of the suggested task to reject
 * @param {string} reason - Optional reason for rejection
 * @returns {Promise<{success: boolean}>} Result of the operation
 */
export const rejectSuggestedTask = async (id: string, reason?: string): Promise<{success: boolean}> => {
  console.log(`[AISuggestionsService] Rejecting suggested task ${id} via Thalassa API`);
  
  try {
    const response = await deiterateAPI.PUT(`${AI_SUGGESTIONS_API}/${id}/reject`, {
      mode: 'cors',
      backend: 'thalassa',
      headers: getHeaders(),
      body: JSON.stringify({ reason }),
    });

    if (response.statusCode === 200) {
      console.log(`[AISuggestionsService] Successfully marked suggestion ${id} as rejected`);
      return { success: true };
    }
    
    console.error('[AISuggestionsService] Error rejecting suggestion:', response.error);
    return { success: false };
  } catch (error) {
    console.error('[AISuggestionsService] Error rejecting suggestion:', error);
    return { success: false };
  }
};

/**
 * Mark a specific task within a suggestion document as accepted or rejected via Thalassa API
 * @param {string} docId - ID of the suggestion document
 * @param {string} taskId - ID/cast_id of the specific task within the suggestion
 * @param {string} status - Status to set ('accepted' or 'rejected')
 * @param {string} reason - Optional reason for rejection
 * @returns {Promise<{success: boolean}>} Result of the operation
 */
export const markSuggestedTaskStatus = async (
  docId: string, 
  taskId: string, 
  status: 'accepted' | 'rejected', 
  reason?: string
): Promise<{success: boolean; error?: string}> => {
  console.log(`[AISuggestionsService] Marking task ${taskId} in document ${docId} as ${status} via Thalassa API`);
  
  try {
    const response = await deiterateAPI.POST(`${AI_SUGGESTIONS_API}/task-status`, {
      mode: 'cors',
      backend: 'thalassa',
      headers: getHeaders(),
      body: JSON.stringify({ docId, taskId, status, reason }),
    });

    if (response.statusCode === 200) {
      console.log(`[AISuggestionsService] Successfully marked task ${taskId} in doc ${docId} as ${status}`);
      return { success: true };
    }
    
    return { success: false, error: response.error || 'Unknown error' };
  } catch (error) {
    console.error(`[AISuggestionsService] Error marking task ${taskId} in doc ${docId} as ${status}:`, error);
    return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
  }
};

/**
 * Mark a specific task within a suggestion document as accepted
 * @param {string} docId - ID of the suggestion document
 * @param {string} taskId - ID/cast_id of the specific task within the suggestion
 * @returns {Promise<{success: boolean}>} Result of the operation
 */
export const acceptSuggestedTaskItem = async (docId: string, taskId: string): Promise<{success: boolean}> => {
  return markSuggestedTaskStatus(docId, taskId, 'accepted');
};

/**
 * Mark a specific task within a suggestion document as rejected
 * @param {string} docId - ID of the suggestion document
 * @param {string} taskId - ID/cast_id of the specific task within the suggestion
 * @param {string} reason - Optional reason for rejection
 * @returns {Promise<{success: boolean}>} Result of the operation
 */
export const rejectSuggestedTaskItem = async (docId: string, taskId: string, reason?: string): Promise<{success: boolean}> => {
  return markSuggestedTaskStatus(docId, taskId, 'rejected', reason);
};
